import { Component, Inject, OnDestroy, OnInit, Optional, PLATFORM_ID } from '@angular/core';
import { Params } from '@angular/router';
import { MediaObserver } from '@angular/flex-layout';
import { select, Store } from '@ngrx/store';
import { takeUntil, shareReplay, distinctUntilChanged, filter, withLatestFrom } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { getQueryParams } from '~core/store';
import {
  getProducts,
  getProductsTotalCountForBrand,
  getBrandInfoContent,
  getSecondLevelCategoriesBrand
} from '~app/modules/brand-plp/store';
import { TitleService } from '~shared/services/title.service';
import { ProductsLoadingService } from '~app/modules/brand-plp/services/products-loading.service';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { ApplicationEventFilterAreaInteractionType } from '~shared/modules/application-event/constants';
import { FilterBarService } from '~shared/services/filter-bar.service';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { FilterAreaInteractionApplicationEvent } from '~shared/modules/application-event/models';
import { Product } from '~shared/models';
import { BrandInfo, StoryblokBrandInfo } from '../../models';
import * as fromBrandPlp from '../../store';
import isEqual from 'lodash.isequal';
import { StoryblokService } from '~app/modules/storyblok/services';
import { SB_BRAND_PLP_ROUTE } from '~app/modules/brand-plp/constants/api.constant';
import { Components } from '~app/modules/storyblok/constants';
import { NUMBER_404, NUMBER_500 } from '~shared/constants';
import { Story, StoryblokComponent, StoryData } from 'storyblok-js-client';
import { ScrollUtils } from '~shared/utils/scroll.utils';
import { isPlatformServer } from '@angular/common';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { getCountryDomainCode } from '~server/utils/country-detection.util';
import { InputsType } from 'ng-dynamic-component';
import { Category } from '~app/modules/generic/models';

@Component({
  selector: 'app-brand-plp',
  templateUrl: './brand-plp.component.html',
  styleUrls: ['./brand-plp.component.scss'],
  providers: [ProductsLoadingService],
})
export class BrandPLPComponent implements OnInit, OnDestroy {
  isMobile = false;
  isTablet = false;
  isDesktop = false;
  isMobileOrTablet = false;
  isLandScapeTablet = false;
  isPageLoading = true;
  isPageLoaded$ = new BehaviorSubject<boolean>(false);
  products$: Observable<Product[]>;
  productsTotalCount$: Observable<number>;
  brandInfo$: Observable<BrandInfo>;
  queryParams$: Observable<Params>;
  isZeroResult$: Observable<boolean>;
  isStickyActions = false;
  isSearchPage = true;
  isProductsLoadedOnce$: Observable<boolean>;
  showBrandPLPLoading$: Observable<boolean>;
  isServer = isPlatformServer(this.platformId);
  categories = [];


  // Storyblok
  storyBrandContent: StoryblokBrandInfo;
  storyTopContent: Array<StoryblokComponent<string>> = [];
  storyBottomContent: Array<StoryblokComponent<string>> = [];
  storyEditable: string;
  storyId: number;
  components = Components;

  private unsubscribe$ = new Subject();

  applicationEventFilterAreaInteractionType = ApplicationEventFilterAreaInteractionType;

  constructor(
    private store$: Store,
    private titleService: TitleService,
    private filterBarService: FilterBarService,
    private mediaObserver: MediaObserver,
    @Inject(PLATFORM_ID) private platformId: Object,
    private applicationEventService: ApplicationEventService,
    private productsLoadingService: ProductsLoadingService,
    private storyblokService: StoryblokService,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any
  ) {
    this.checkContentOnStoryblokEdit();
  }

  ngOnInit(): void {
    this.isPageLoading = true;
    this.initializeTitlePlaceholder();
    this.initializeMediaObservable();
    this.initializeProducts();
    this.initializeBrandPLPLoading();
    this.initializeBrandInfo();
    this.initializeQueryParams();
    this.initializeScrollToProductsCheck();
  }

  ngOnDestroy(): void {
    this.productsLoadingService.clearTimers();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.store$.dispatch(new fromBrandPlp.ResetProducts());
    this.store$.dispatch(new fromBrandPlp.ResetBrandInfo());
  }

  toggleFilters(isOpened: boolean, type: ApplicationEventFilterAreaInteractionType): void {
    this.filterBarService.toggleFilterBar(isOpened);
    this.fireFilterInteractionEvent(type);
  }

  buildInputs(blokInputs: InputsType): InputsType {
    return {
      ...blokInputs,
      ...{
        isMobile: this.isMobile,
        isTablet: this.isTablet,
        isDesktop: this.isDesktop,
      },
    };
  }

  private initializeTitlePlaceholder(): void {
    this.titleService.setTitle([{ title: 'TITLE.PAGE.CATEGORY' }]);
  }

  private initializeProducts(): void {
    this.products$ = this.store$.pipe(select(getProducts));
    this.productsTotalCount$ = this.store$.pipe(select(getProductsTotalCountForBrand));
  }

  private initializeBrandPLPLoading(): void {
    this.showBrandPLPLoading$ = this.productsLoadingService.showProductsLoading$;
    this.isProductsLoadedOnce$ = this.productsLoadingService.isProductsLoadedOnce$;
  }

  private initializeBrandInfo(): void {
    this.store$
      .pipe(select(getBrandInfoContent), distinctUntilChanged(isEqual), withLatestFrom(this.store$.pipe(select(getSecondLevelCategoriesBrand))), takeUntil(this.unsubscribe$))
      .subscribe(([brandInfo, categories]: [BrandInfo, Category[]]) => {
        if (brandInfo) {
          this.categories = categories
          this.brandInfo$ = of(brandInfo);

          this.titleService.setTitle([{ title: 'TITLE.PAGE.LANDING_PAGE', slug: brandInfo?.name }]);
          this.loadStoryblokDynamicContent(brandInfo.slug);
        }
      });
  }

  private initializeMediaObservable(): void {
    this.mediaObserver
      .asObservable()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.isMobile = this.mediaObserver.isActive(MEDIA_ALISES.mobile);
        this.isTablet = this.mediaObserver.isActive(MEDIA_ALISES.tablet);
        this.isDesktop = this.mediaObserver.isActive(MEDIA_ALISES.desktop);
        this.isLandScapeTablet = this.mediaObserver.isActive(MEDIA_ALISES.tabletLandscape);
        this.isMobileOrTablet = this.isMobile || this.isTablet;
      });
  }

  private initializeQueryParams(): void {
    this.queryParams$ = this.store$.pipe(select(getQueryParams), shareReplay(1));
  }

  private initializeScrollToProductsCheck(): void {
    combineLatest([this.isPageLoaded$, this.queryParams$])
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(([isPageLoaded, queryParams]) => isPageLoaded && !!queryParams.offset)
      )
      .subscribe(() => {
        setTimeout(() => ScrollUtils.scrollToFirstOfClass('brand-plp-body', 'smooth'), NUMBER_500);
      });
  }

  private fireFilterInteractionEvent(type: ApplicationEventFilterAreaInteractionType): void {
    this.applicationEventService.publish(new FilterAreaInteractionApplicationEvent(type));
  }

  private async loadStoryblokDynamicContent(pageSlug: string): Promise<void> {
    this.isPageLoading = true;
    this.clearStoryData();

    try {
      const story = await this.fetchStory(pageSlug);
      this.processStory(story);
    } catch (error) {
      this.handleError(error);
      this.clearStoryData();
    } finally {
      this.isPageLoading = false;
      this.isPageLoaded$.next(true);
    }
  }

  private async fetchStory(pageSlug: string): Promise<Story> {
    const market = getCountryDomainCode(this.webAppBuyerRequest);
    const storySlug = `${SB_BRAND_PLP_ROUTE}/${pageSlug}`.replace('{market}', market);
    return this.storyblokService.getStory(storySlug);
  }

  private processStory(story: Story): void {
    if (story?.data?.story) {
      const { id, content } = story.data.story;
      this.storyId = id;
      this.storyEditable = content._editable;
      this.assignStoryData(story.data.story);
    } else {
      this.clearStoryData();
    }
  }

  private handleError(error: any): void {
    if (error?.response?.status !== NUMBER_404) {
      console.error(error);
    }
  }

  private clearStoryData(): void {
    this.storyId = null;
    this.storyEditable = null;
    this.assignStoryData({ content: {} } as StoryData);
  }

  private checkContentOnStoryblokEdit(): void {
    window?.storyblok?.on('input', (event: StoryblokEventPayload) => {
      window.storyblok.addComments(event.story.content, String(event.story.id));
      if (event.story.id === this.storyId) {
        this.assignStoryData(event.story);
      }
    });
  }

  private assignStoryData(storyData: StoryData | null): void {
    const safeData = storyData?.content || {} as any;

    this.storyTopContent = [...[], ...(safeData.topContent || [])];
    this.storyBottomContent = [...[], ...(safeData.bottomContent || [])];
    this.storyBrandContent = safeData.brandInformation?.[0] || null;
  }
}
